import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, CircularProgress, Typography, IconButton, Tooltip
} from '@mui/material';
import { Print, Close, Edit, Save, Cancel, Refresh, Add, Delete } from '@mui/icons-material';
import { patientAPI, ipdAPI } from '../services/api';
import { useReactToPrint } from 'react-to-print';

/* ── helpers ─────────────────────────────────────────── */
const fmt = (date) =>
  date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const calcAge = (dob) => {
  if (!dob) return '—';
  const b = new Date(dob), t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
  return age;
};

const calcBMI = (weight, height) => {
  if (!weight || !height) return null;
  return (weight / Math.pow(height / 100, 2)).toFixed(1);
};

/* ── pain scale ──────────────────────────────────────── */
const PAIN_FACES  = ['😄', '😊', '🙂', '😐', '😕', '😟', '😢', '😣', '😖', '😭', '😱'];
const PAIN_LABELS = ['No\nHurt', 'Hurts\nLittle Bit', 'Hurts\nLittle More', 'Hurts\nEven More', 'Hurts\nWhole Lot', 'Hurts\nWorst'];

/* ── blank dot lines ─────────────────────────────────── */
const BlankLines = ({ n = 3 }) => (
  <div style={{ marginTop: 4 }}>
    {Array.from({ length: n }).map((_, i) => (
      <div key={i} style={{ borderBottom: '1px dotted #555', margin: '8px 0', minHeight: 18 }} />
    ))}
  </div>
);

/* ── filled lines (text with dotted underlines) ─────── */
const FilledLines = ({ text }) => {
  if (!text) return <BlankLines />;
  return (
    <div style={{ margin: '6px 0 4px 0', lineHeight: 1.7, fontSize: 11 }}>
      {text.split('\n').map((line, i) => (
        <div key={i} style={{ borderBottom: '1px dotted #aaa', paddingBottom: 2, minHeight: 18 }}>{line || ' '}</div>
      ))}
    </div>
  );
};

/* ── edit textarea (hidden on print) ────────────────── */
const EditArea = ({ value, onChange, rows = 3, placeholder = '' }) => (
  <div className="no-print" style={{ margin: '4px 0 8px 0' }}>
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      style={{
        width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 11,
        padding: '6px 8px', border: '1.5px solid #2e7d32', borderRadius: 4,
        resize: 'vertical', boxSizing: 'border-box'
      }}
    />
  </div>
);

/* ── small inline input (for table cells in edit mode) ── */
const CellInput = ({ value, onChange, placeholder = '' }) => (
  <input
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    style={{
      width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 10,
      padding: '2px 4px', border: '1px solid #aaa', borderRadius: 3, boxSizing: 'border-box'
    }}
  />
);

const EMPTY_TI  = { treatmentName: '', frequency: '', duration: '', instructions: '' };
const EMPTY_MED = { medicineName: '', dosage: '', frequency: '', duration: '', route: '', instructions: '' };

/* ══════════════════════════════════════════════════════ */
function DischargeSummaryPrint({ admissionId, onClose }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const printRef = useRef();

  /* ── edit state ─────────────────────────────────────── */
  const [editMode, setEditMode] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [editData, setEditData] = useState({
    diagnosis: '', courseNotes: '', investigations: '', treatmentGiven: '',
    improvementPercentage: null, improvementText: '', yogaSuggested: '',
    treatmentInstructions: [],   // [{treatmentName, frequency, duration, instructions}]
    medicines: [],               // [{medicineName, dosage, frequency, duration, route, instructions}]
  });

  useEffect(() => { loadSummary(); }, [admissionId]);

  /* ── init editData from loaded summary ──────────────── */
  const initEditData = (data) => {
    const dp  = data?.dischargePrescription;
    // Pre-fill treatmentGiven from auto-aggregate if no saved override
    const autoTx = Object.entries(
      (data?.treatments || []).reduce((acc, t) => {
        if (t.treatment_name) acc[t.treatment_name] = (acc[t.treatment_name] || 0) + 1;
        return acc;
      }, {})
    ).sort((a, b) => b[1] - a[1]).map(([n, c]) => `${n} (${c})`).join('\n');

    setEditData({
      diagnosis:    dp?.diagnosis             || data?.admission?.reason || '',
      courseNotes:  dp?.notes                 || '',
      investigations: dp?.investigations      || '',
      treatmentGiven: dp?.treatment_given_notes || autoTx,
      improvementPercentage: dp?.improvement_percentage ?? null,
      improvementText: dp?.improvement_text   || '',
      yogaSuggested: dp?.yoga_suggested       || '',
      treatmentInstructions: (dp?.treatmentInstructions || []).map(t => ({
        treatmentName: t.treatment_name, frequency: t.frequency || '',
        duration: t.duration || '',      instructions: t.instructions || '',
      })),
      medicines: (dp?.medicines || []).map(m => ({
        medicineName: m.medicine_name, dosage: m.dosage || '',
        frequency: m.frequency || '',   duration: m.duration || '',
        route: m.route || '',           instructions: m.instructions || '',
      })),
    });
  };

  const loadSummary = async () => {
    setLoading(true); setError('');
    try {
      const res = await patientAPI.getDischargeSummary(admissionId);
      const data = res.data.data;
      setSummary(data);
      initEditData(data);
    } catch { setError('Failed to load discharge summary'); }
    finally  { setLoading(false); }
  };

  const handleEnterEdit = () => { initEditData(summary); setEditMode(true); };

  const handleSaveEdit = async () => {
    setSaving(true);
    const dp = summary?.dischargePrescription;
    try {
      await ipdAPI.createDischargePrescription(admissionId, {
        diagnosis:           editData.diagnosis,
        notes:               editData.courseNotes,
        investigations:      editData.investigations,
        treatmentGivenNotes: editData.treatmentGiven,
        yogaSuggested:       editData.yogaSuggested,
        improvementText:     editData.improvementText,
        improvementPercentage: editData.improvementPercentage !== null
          ? Number(editData.improvementPercentage) : null,
        followUpDate:    dp?.follow_up_date ? dp.follow_up_date.split('T')[0] : null,
        followUpContact: dp?.follow_up_contact || null,
        medicines: editData.medicines,
        dietInstructions: (dp?.dietInstructions || []).map(d => ({
          instruction: d.instruction, category: d.category,
        })),
        treatmentInstructions: editData.treatmentInstructions,
      });
      setEditMode(false);
      await loadSummary();
    } catch (err) { console.error('Save error:', err); }
    finally { setSaving(false); }
  };

  /* ── treatment instruction row helpers ─────────────── */
  const setTI = (i, f, v) => setEditData(d => {
    const a = [...d.treatmentInstructions]; a[i] = { ...a[i], [f]: v };
    return { ...d, treatmentInstructions: a };
  });
  const addTI    = () => setEditData(d => ({ ...d, treatmentInstructions: [...d.treatmentInstructions, { ...EMPTY_TI }] }));
  const removeTI = (i) => setEditData(d => ({ ...d, treatmentInstructions: d.treatmentInstructions.filter((_, j) => j !== i) }));

  /* ── medicine row helpers ───────────────────────────── */
  const setMed    = (i, f, v) => setEditData(d => {
    const a = [...d.medicines]; a[i] = { ...a[i], [f]: v };
    return { ...d, medicines: a };
  });
  const addMed    = () => setEditData(d => ({ ...d, medicines: [...d.medicines, { ...EMPTY_MED }] }));
  const removeMed = (i) => setEditData(d => ({ ...d, medicines: d.medicines.filter((_, j) => j !== i) }));

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `DischargeSummary_${summary?.admission?.patient_code}_${new Date().toISOString().split('T')[0]}`
  });

  /* ── loading / error states ─────────────────────────── */
  if (loading) return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogContent>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </DialogContent>
    </Dialog>
  );

  if (error || !summary) return (
    <Dialog open onClose={onClose}>
      <DialogContent>
        <Typography color="error">{error || 'No data available'}</Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );

  const { admission, treatments, dietPlans, vitals, prognosis, dischargePrescription: dp } = summary;

  /* ── vitals ─────────────────────────────────────────── */
  const v   = vitals && vitals.length > 0 ? vitals[0] : null;
  const bmi = v ? calcBMI(v.weight, v.height) : null;

  /* ── derived display values (view mode) ─────────────── */
  const diagnosis           = dp?.diagnosis || admission.reason || '';
  const courseNotes         = dp?.notes || '';
  const investigations      = dp?.investigations || '';
  const treatmentGivenNotes = dp?.treatment_given_notes || '';
  const improvement         = dp?.improvement_percentage;
  const improvementText     = dp?.improvement_text || '';
  const yogaText            = dp?.yoga_suggested || '';
  const treatmentInstructions = dp?.treatmentInstructions || [];
  const suggestedMedicines    = dp?.medicines || [];
  const doctorName          = dp?.doctor_name || admission.admitted_by_name || '';

  /* ── IPD treatment counts (auto-aggregate) ──────────── */
  const treatmentCountEntries = Object.entries(
    treatments.reduce((acc, t) => {
      if (t.treatment_name) acc[t.treatment_name] = (acc[t.treatment_name] || 0) + 1;
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  /* ── diet item counts (auto-aggregate) ──────────────── */
  const dietItemEntries = Object.entries(
    dietPlans.reduce((acc, plan) => {
      if (plan.items) plan.items.split(', ').forEach(item => {
        const n = item.trim(); if (n) acc[n] = (acc[n] || 0) + 1;
      });
      return acc;
    }, {})
  ).sort((a, b) => b[1] - a[1]);

  /* ── pain score ─────────────────────────────────────── */
  const effImprovement = editMode ? editData.improvementPercentage : improvement;
  const painScore = effImprovement !== null && effImprovement !== undefined
    ? Math.round((1 - effImprovement / 100) * 10) : null;

  /* ─────────────────────── sub-components ────────────── */
  const PageHeader = () => (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: '2px solid #2e7d32', paddingBottom: 8, marginBottom: 6 }}>
      <div style={{ width: 90, textAlign: 'center', flexShrink: 0 }}>
        <img src="/logo.png" alt="MSG Logo" style={{ width: 75, height: 75, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
      </div>
      <div style={{ flex: 1, textAlign: 'center', padding: '0 6px' }}>
        <div style={{ fontSize: 18, fontWeight: 900, color: '#1b5e20', letterSpacing: 1 }}>MSG NATUROPATHY CENTER</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#2e7d32', marginTop: 2 }}>(Managed by ARZ Unique Enterprises Limited)</div>
        <div style={{ fontSize: 10, color: '#444', marginTop: 3 }}>Sirsa, Haryana</div>
        <div style={{ fontSize: 10, color: '#444' }}>📞 +91 7082999551 / 7082999351 &nbsp;|&nbsp; msgelite7wonders@gmail.com</div>
      </div>
      <div style={{ width: 90, flexShrink: 0 }}></div>
    </div>
  );

  const SectionBar = ({ title }) => (
    <div style={{
      background: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: 11,
      padding: '3px 8px', marginTop: 10, marginBottom: 4, letterSpacing: 0.5
    }}>{title}</div>
  );

  /* ─────────────────────────── render ─────────────────── */
  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: '95vh' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Discharge Summary — {admission.first_name} {admission.last_name}</span>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh"><IconButton size="small" onClick={loadSummary}><Refresh fontSize="small" /></IconButton></Tooltip>
          {!editMode ? (
            <Tooltip title="Edit summary">
              <IconButton size="small" color="primary" onClick={handleEnterEdit}><Edit fontSize="small" /></IconButton>
            </Tooltip>
          ) : (
            <>
              <IconButton size="small" color="error" onClick={() => setEditMode(false)}><Cancel fontSize="small" /></IconButton>
              <Button size="small" variant="contained" color="success"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Save />}
                onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <div ref={printRef}>
          <style>{`
            @media print {
              @page { size: A4; margin: 12mm 14mm; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .no-print { display: none !important; }
              .page-break { page-break-before: always; break-before: page; }
            }
            .ds-wrap { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 16px 20px; }
            table.info-table { width: 100%; border-collapse: collapse; font-size: 11px; }
            table.info-table td { padding: 3px 6px; vertical-align: top; }
            table.vitals-table { width: 100%; border-collapse: collapse; font-size: 10px; }
            table.vitals-table th, table.vitals-table td { border: 1px solid #333; padding: 4px 6px; text-align: center; }
            table.vitals-table th { background: #e8f5e9; font-weight: 700; }
          `}</style>

          <div className="ds-wrap">

            {/* ══════════════ PAGE 1 ══════════════ */}
            <PageHeader />

            {/* Patient Info */}
            <table className="info-table" style={{ marginTop: 8, borderTop: '1px solid #ccc', borderBottom: '1px solid #ccc' }}>
              <tbody>
                <tr>
                  <td style={{ width: '55%', verticalAlign: 'top', borderRight: '1px solid #ccc', paddingRight: 12 }}>
                    <table style={{ width: '100%', fontSize: 11 }}><tbody>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600, width: 120 }}>Patient Name</td>
                          <td style={{ paddingBottom: 4 }}>: <strong>{admission.first_name} {admission.last_name}</strong></td></tr>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600 }}>Age / Sex / Mobile</td>
                          <td style={{ paddingBottom: 4 }}>: {calcAge(admission.date_of_birth)} Yrs / {admission.gender || '—'} / {admission.phone || '—'}</td></tr>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600 }}>Address</td>
                          <td style={{ paddingBottom: 4 }}>: {admission.address || '—'}</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Doctor's Name</td>
                          <td>: {doctorName || '—'}</td></tr>
                    </tbody></table>
                  </td>
                  <td style={{ width: '45%', verticalAlign: 'top', paddingLeft: 12 }}>
                    <table style={{ width: '100%', fontSize: 11 }}><tbody>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600, width: 130 }}>UHID No.</td>
                          <td style={{ paddingBottom: 4 }}>: <strong>{admission.patient_code}</strong></td></tr>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600 }}>IPD No.</td>
                          <td style={{ paddingBottom: 4 }}>: {admission.id}</td></tr>
                      <tr><td style={{ paddingBottom: 4, fontWeight: 600 }}>Admission Date</td>
                          <td style={{ paddingBottom: 4 }}>: {fmt(admission.admission_date)}</td></tr>
                      <tr><td style={{ fontWeight: 600 }}>Discharge Date</td>
                          <td>: {fmt(admission.discharge_date)}</td></tr>
                    </tbody></table>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Title bar */}
            <div style={{ textAlign: 'center', background: '#1b5e20', color: '#fff', fontWeight: 900, fontSize: 13, padding: '5px 0', marginTop: 8, letterSpacing: 1 }}>
              DISCHARGE SUMMARY AND FOLLOWUP PLAN
            </div>

            {/* VITAL DATA */}
            <SectionBar title="VITAL DATA" />
            <table className="vitals-table">
              <thead><tr>
                <th>BP<br />(mmHg)</th><th>PULSE<br />(bpm)</th><th>RESPIRATION<br />(/min)</th>
                <th>TEMP<br />(°F)</th><th>HEIGHT<br />(cm)</th><th>WEIGHT<br />(kg)</th>
                <th>BMI<br />(kg/m²)</th><th>SPO2<br />(%)</th>
              </tr></thead>
              <tbody><tr>
                <td>{v?.blood_pressure || <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.pulse_rate || <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.respiratory_rate || <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.temperature ? `${v.temperature}°F` : <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.height ? `${v.height} cm` : <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.weight ? `${v.weight} kg` : <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{bmi || <span style={{ color: '#aaa' }}>........</span>}</td>
                <td>{v?.oxygen_saturation ? `${v.oxygen_saturation}%` : <span style={{ color: '#aaa' }}>........</span>}</td>
              </tr></tbody>
            </table>

            {/* DAILY PROGNOSIS REPORT */}
            <SectionBar title="DAILY PROGNOSIS REPORT" />
            {!prognosis || prognosis.length === 0 ? (
              <div style={{ fontSize: 10, fontStyle: 'italic', color: '#666', padding: '6px 8px', border: '1px solid #333', textAlign: 'center' }}>
                No prognosis records recorded during this admission
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                <thead>
                  <tr style={{ background: '#e8f5e9' }}>
                    <th style={{ border: '1px solid #333', padding: '4px 6px', textAlign: 'left', width: '120px' }}>Date & Time</th>
                    <th style={{ border: '1px solid #333', padding: '4px 6px', textAlign: 'left' }}>Progressive Note</th>
                    <th style={{ border: '1px solid #333', padding: '4px 6px', textAlign: 'left' }}>Doctor's Order</th>
                    <th style={{ border: '1px solid #333', padding: '4px 6px', textAlign: 'left', width: '120px' }}>Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {prognosis.map((p, idx) => (
                    <tr key={idx}>
                      <td style={{ border: '1px solid #333', padding: '4px 6px', whiteSpace: 'nowrap' }}>
                        {new Date(p.recorded_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td style={{ border: '1px solid #333', padding: '4px 6px', whiteSpace: 'pre-wrap' }}>{p.progressive_note || '-'}</td>
                      <td style={{ border: '1px solid #333', padding: '4px 6px', whiteSpace: 'pre-wrap' }}>{p.doctors_order || '-'}</td>
                      <td style={{ border: '1px solid #333', padding: '4px 6px' }}>
                        {p.recorded_by_name || '-'} {p.recorded_by_role ? `(${p.recorded_by_role})` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* INVESTIGATIONS + PAIN SCALE */}
            <div style={{ display: 'flex', gap: 12, marginTop: 10 }}>
              <div style={{ flex: 1, border: '1px solid #333', padding: '4px 8px' }}>
                <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 4, background: '#e8f5e9', padding: '2px 4px' }}>
                  INVESTIGATIONS
                </div>
                {editMode ? (
                  <EditArea
                    value={editData.investigations}
                    onChange={v => setEditData(d => ({ ...d, investigations: v }))}
                    rows={4} placeholder="Enter investigations..."
                  />
                ) : (
                  <FilledLines text={investigations} />
                )}
                {!editMode && !investigations && <BlankLines n={2} />}
              </div>

              <div style={{ width: 280, border: '1px solid #333', padding: '4px 8px' }}>
                <div style={{ fontWeight: 700, fontSize: 11, marginBottom: 6, background: '#e8f5e9', padding: '2px 4px', textAlign: 'center' }}>
                  PAIN MEASUREMENT SCALE
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  {PAIN_FACES.map((face, i) => (
                    <div key={i} style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ fontSize: painScore === i ? 20 : 14, lineHeight: 1, filter: painScore !== null && painScore !== i ? 'grayscale(1) opacity(0.4)' : 'none' }}>{face}</div>
                      <div style={{ fontSize: 8, lineHeight: 1, marginTop: 1 }}>{i}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                  {PAIN_LABELS.map((label, i) => (
                    <div key={i} style={{ textAlign: 'center', fontSize: 7, color: '#555', lineHeight: 1.2, flex: 1, whiteSpace: 'pre-line' }}>{label}</div>
                  ))}
                </div>
                {painScore !== null && (
                  <div style={{ textAlign: 'center', fontSize: 9, marginTop: 4, color: '#1b5e20', fontWeight: 700 }}>
                    Pain Score at Discharge: {painScore}/10
                  </div>
                )}
              </div>
            </div>

            {/* COURSE IN HOSPITAL */}
            <SectionBar title="COURSE IN HOSPITAL" />
            {editMode ? (
              <EditArea value={editData.courseNotes}
                onChange={v => setEditData(d => ({ ...d, courseNotes: v }))}
                rows={4} placeholder="Notes about patient's hospital course..." />
            ) : (
              <FilledLines text={courseNotes || (
                `Patient was admitted on ${fmt(admission.admission_date)} with ${diagnosis || 'chief complaints'}.` +
                (treatmentCountEntries.length > 0 ? ` Received naturopathy treatments including ${treatmentCountEntries.slice(0, 5).map(([n]) => n).join(', ')}.` : '') +
                ` The patient's health status at the time of discharge is stable with satisfactory improvement.` +
                (improvement !== null && improvement !== undefined ? ` Patient showed ${improvement}% improvement.` : '')
              )} />
            )}

            {/* TREATMENT GIVEN */}
            <SectionBar title="TREATMENT GIVEN" />
            {editMode ? (
              <EditArea
                value={editData.treatmentGiven}
                onChange={v => setEditData(d => ({ ...d, treatmentGiven: v }))}
                rows={5} placeholder="List treatments given (one per line)..."
              />
            ) : treatmentGivenNotes ? (
              <FilledLines text={treatmentGivenNotes} />
            ) : treatmentCountEntries.length > 0 ? (
              <div style={{ padding: '5px 6px', fontSize: 11, lineHeight: 1.9 }}>
                {treatmentCountEntries.map(([name, count], i) => (
                  <span key={name}>
                    {name} <strong>({count})</strong>{i < treatmentCountEntries.length - 1 ? ',  ' : ''}
                  </span>
                ))}
              </div>
            ) : <BlankLines n={2} />}

            {/* DIET GIVEN */}
            <SectionBar title="DIET GIVEN" />
            {dietItemEntries.length > 0 ? (
              <div style={{ padding: '5px 6px', fontSize: 11, lineHeight: 1.9 }}>
                {dietItemEntries.map(([name, count], i) => (
                  <span key={name}>
                    {name} <strong>({count})</strong>{i < dietItemEntries.length - 1 ? ',  ' : ''}
                  </span>
                ))}
              </div>
            ) : <BlankLines n={2} />}

            {/* DIAGNOSIS */}
            <SectionBar title="DIAGNOSIS" />
            {editMode ? (
              <EditArea value={editData.diagnosis}
                onChange={v => setEditData(d => ({ ...d, diagnosis: v }))}
                rows={3} placeholder="Patient's diagnosis..." />
            ) : <FilledLines text={diagnosis} />}

            {/* ══════════════ PAGE 2 ══════════════ */}
            <div className="page-break" />
            <PageHeader />
            <div style={{ textAlign: 'center', background: '#1b5e20', color: '#fff', fontWeight: 900, fontSize: 13, padding: '5px 0', marginTop: 8, letterSpacing: 1 }}>
              DISCHARGE SUMMARY AND FOLLOWUP PLAN (Contd.)
            </div>

            {/* DISCHARGE IMPROVEMENT */}
            <SectionBar title="DISCHARGE IMPROVEMENT" />
            {editMode ? (
              <div className="no-print" style={{ padding: '8px 8px 4px 8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>Overall Improvement (%):</span>
                  <input
                    type="range" min={0} max={100} step={5}
                    value={editData.improvementPercentage ?? 0}
                    onChange={e => setEditData(d => ({ ...d, improvementPercentage: Number(e.target.value) }))}
                    style={{ flex: 1, accentColor: '#2e7d32' }}
                  />
                  <input
                    type="number" min={0} max={100}
                    value={editData.improvementPercentage ?? ''}
                    onChange={e => setEditData(d => ({ ...d, improvementPercentage: e.target.value === '' ? null : Math.min(100, Math.max(0, Number(e.target.value))) }))}
                    style={{ width: 60, padding: '3px 6px', border: '1.5px solid #2e7d32', borderRadius: 4, fontSize: 13, fontWeight: 700, textAlign: 'center' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#2e7d32' }}>%</span>
                </div>
                <textarea
                  value={editData.improvementText}
                  onChange={e => setEditData(d => ({ ...d, improvementText: e.target.value }))}
                  rows={3}
                  placeholder="Describe overall improvement in detail..."
                  style={{ width: '100%', fontFamily: 'Arial, sans-serif', fontSize: 11, padding: '6px 8px', border: '1.5px solid #2e7d32', borderRadius: 4, resize: 'vertical', boxSizing: 'border-box' }}
                />
              </div>
            ) : null}
            {effImprovement !== null && effImprovement !== undefined ? (
              <div style={{ padding: '6px 8px', fontSize: 11 }}>
                <span style={{ fontWeight: 700 }}>Overall Improvement: </span>
                <span style={{ fontSize: 14, fontWeight: 900, color: '#1b5e20' }}>{effImprovement}%</span>
                <div style={{ marginTop: 6 }}>
                  <div style={{ background: '#e0e0e0', borderRadius: 4, height: 14, overflow: 'hidden' }}>
                    <div style={{ background: '#2e7d32', width: `${effImprovement}%`, height: '100%', borderRadius: 4 }} />
                  </div>
                </div>
              </div>
            ) : !editMode ? null : null}
            {(editMode ? editData.improvementText : improvementText) ? (
              <div style={{ padding: '4px 8px 6px 8px', fontSize: 11 }}>
                <FilledLines text={editMode ? editData.improvementText : improvementText} />
              </div>
            ) : !editMode ? <BlankLines n={3} /> : null}

            {/* SUGGESTED TREATMENT */}
            <SectionBar title="SUGGESTED TREATMENT" />
            {editMode ? (
              <div className="no-print" style={{ margin: '4px 0 8px 0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead><tr style={{ background: '#f1f8e9' }}>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #ccc', width: '30%' }}>Treatment</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #ccc', width: '20%' }}>Frequency</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #ccc', width: '15%' }}>Duration</th>
                    <th style={{ textAlign: 'left', padding: '3px 6px', borderBottom: '1px solid #ccc' }}>Instructions</th>
                    <th style={{ padding: '3px 6px', borderBottom: '1px solid #ccc', width: 32 }}></th>
                  </tr></thead>
                  <tbody>
                    {editData.treatmentInstructions.map((t, i) => (
                      <tr key={i}>
                        <td style={{ padding: '2px 4px' }}><CellInput value={t.treatmentName} onChange={v => setTI(i, 'treatmentName', v)} placeholder="Treatment name" /></td>
                        <td style={{ padding: '2px 4px' }}><CellInput value={t.frequency}     onChange={v => setTI(i, 'frequency', v)}     placeholder="e.g. Daily" /></td>
                        <td style={{ padding: '2px 4px' }}><CellInput value={t.duration}      onChange={v => setTI(i, 'duration', v)}      placeholder="e.g. 1 month" /></td>
                        <td style={{ padding: '2px 4px' }}><CellInput value={t.instructions}  onChange={v => setTI(i, 'instructions', v)}  placeholder="Notes" /></td>
                        <td style={{ padding: '2px 4px', textAlign: 'center' }}>
                          <button onClick={() => removeTI(i)} style={{ border: 'none', background: 'none', color: '#c62828', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={addTI} style={{ marginTop: 4, padding: '3px 10px', background: '#e8f5e9', border: '1px solid #2e7d32', borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#1b5e20', fontWeight: 600 }}>
                  + Add Treatment
                </button>
              </div>
            ) : treatmentInstructions.length > 0 ? (
              <FilledLines text={treatmentInstructions
                .map(t => `${t.treatment_name}${t.frequency ? ` — ${t.frequency}` : ''}${t.duration ? `, ${t.duration}` : ''}${t.instructions ? ` (${t.instructions})` : ''}`)
                .join('\n')} />
            ) : <BlankLines n={4} />}

            {/* SUGGESTED MEDICINES */}
            <SectionBar title="SUGGESTED MEDICINES" />
            {editMode ? (
              <div className="no-print" style={{ margin: '4px 0 8px 0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                  <thead><tr style={{ background: '#f1f8e9' }}>
                    {['Medicine', 'Dosage', 'Frequency', 'Duration', 'Route', 'Instructions', ''].map((h, i) => (
                      <th key={i} style={{ textAlign: 'left', padding: '3px 5px', borderBottom: '1px solid #ccc', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {editData.medicines.map((m, i) => (
                      <tr key={i}>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.medicineName}  onChange={v => setMed(i, 'medicineName', v)}  placeholder="Name" /></td>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.dosage}        onChange={v => setMed(i, 'dosage', v)}        placeholder="Dosage" /></td>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.frequency}     onChange={v => setMed(i, 'frequency', v)}     placeholder="Frequency" /></td>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.duration}      onChange={v => setMed(i, 'duration', v)}      placeholder="Duration" /></td>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.route}         onChange={v => setMed(i, 'route', v)}         placeholder="Route" /></td>
                        <td style={{ padding: '2px 3px' }}><CellInput value={m.instructions}  onChange={v => setMed(i, 'instructions', v)}  placeholder="Notes" /></td>
                        <td style={{ padding: '2px 3px', textAlign: 'center' }}>
                          <button onClick={() => removeMed(i)} style={{ border: 'none', background: 'none', color: '#c62828', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button onClick={addMed} style={{ marginTop: 4, padding: '3px 10px', background: '#e8f5e9', border: '1px solid #2e7d32', borderRadius: 4, cursor: 'pointer', fontSize: 11, color: '#1b5e20', fontWeight: 600 }}>
                  + Add Medicine
                </button>
              </div>
            ) : suggestedMedicines.length > 0 ? (
              <div style={{ margin: '4px 0', fontSize: 11 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f1f8e9' }}>
                    {['#', 'Medicine', 'Dosage', 'Frequency', 'Duration', 'Route', 'Instructions'].map((h, i) => (
                      <th key={i} style={{ textAlign: i === 0 ? 'center' : 'left', padding: '3px 6px', fontWeight: 700, borderBottom: '1px solid #ccc' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {suggestedMedicines.map((m, i) => (
                      <tr key={i} style={{ borderBottom: '1px dotted #ddd' }}>
                        <td style={{ textAlign: 'center', padding: '2px 6px' }}>{i + 1}</td>
                        <td style={{ padding: '2px 6px', fontWeight: 600 }}>{m.medicine_name}</td>
                        <td style={{ padding: '2px 6px' }}>{m.dosage || '—'}</td>
                        <td style={{ padding: '2px 6px' }}>{m.frequency || '—'}</td>
                        <td style={{ padding: '2px 6px' }}>{m.duration || '—'}</td>
                        <td style={{ padding: '2px 6px' }}>{m.route || '—'}</td>
                        <td style={{ padding: '2px 6px' }}>{m.instructions || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <BlankLines n={4} />}

            {/* SUGGESTED YOGA */}
            <SectionBar title="SUGGESTED YOGA" />
            {editMode ? (
              <EditArea value={editData.yogaSuggested}
                onChange={v => setEditData(d => ({ ...d, yogaSuggested: v }))}
                rows={4} placeholder="Yoga & exercise recommendations..." />
            ) : <FilledLines text={yogaText} />}

            {/* FOLLOW UP */}
            <SectionBar title="FOLLOW UP CONTACT DETAILS" />
            <div style={{ padding: '6px 10px', fontSize: 11, lineHeight: 1.8 }}>
              <div>• For appointment and follow-up query: <strong>+91 7082999551 / 7082999351</strong> (Timing: 8 AM – 5 PM)</div>
              <div>• Email: <strong>msgelite7wonders@gmail.com</strong></div>
              <div>• Please continue practicing the yoga techniques taught during your stay at home.</div>
              <div>• Please continue diet therapy as advised by our dietitian.</div>
            </div>

            {/* Signatures */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 36, padding: '0 10px' }}>
              <div style={{ textAlign: 'center', width: '40%' }}>
                <div style={{ borderTop: '1.5px solid #333', paddingTop: 4, marginTop: 30 }}>Patient / Attender Signature</div>
              </div>
              <div style={{ textAlign: 'center', width: '40%' }}>
                <div style={{ fontSize: 10, marginBottom: 2 }}>{doctorName}</div>
                <div style={{ borderTop: '1.5px solid #333', paddingTop: 4, marginTop: 26 }}>Doctor's Signature &amp; Stamp</div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ textAlign: 'center', color: '#c62828', fontWeight: 700, fontSize: 12, marginTop: 20, borderTop: '1px solid #ccc', paddingTop: 8 }}>
              Note — Not For Medico Legal Purpose
            </div>

          </div>{/* ds-wrap */}
        </div>{/* printRef */}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} startIcon={<Close />} variant="outlined">Close</Button>
        <Button onClick={handlePrint} startIcon={<Print />} variant="contained" color="success" disabled={editMode}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DischargeSummaryPrint;
